import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  Target, 
  User, 
  Sparkles, 
  ArrowRight, 
  CheckCircle2, 
  Rocket, 
  BrainCircuit, 
  Trophy,
  Briefcase,
  Calendar,
  Mail,
  Linkedin,
  Github,
  Globe,
  AlertTriangle,
  RefreshCw,
  Award,
  AlertCircle
} from 'lucide-react';
import { CopyableId } from '@/components/common';
import { profileApi } from '@/services/profileApi';
import fiveIcon from '@/assets/five-icon.png';
import { learningPlanApi, userLearningPlanApi } from '@/services/learningManagementApi';
import type { UserProfile, Bio } from '@/types/userProfile';
import type { LearningPlan, ActivePlanDashboardResponse } from '@/types/learningManagement';
import { CoachJourneySummary } from '@/pages/coach/CoachJourneySummary';

const SESSION_STORAGE_KEY = 'five_ai_onboarding_session_id';
const LOCAL_USER_INFO_KEY = 'five_ai_local_user_info';

const SETTING_UP_MESSAGES = [
  "Hang tight — we're setting things up...",
  "Creating your personalized profile...",
  "Generating your AI-powered bio...",
  "Building your learning plan...",
  "Almost there...",
  "Preparing your career journey...",
  "Setting up your AI coach...",
  "Just a moment — greatness in progress...",
  "Tailoring everything for you...",
];

/** Learning path is only "ready" when it has actual content (modules or details). */
function hasLearningPathContent(p: LearningPlan | null | undefined): boolean {
  if (!p) return false;
  const extended = p as unknown as Record<string, unknown>;
  const mods = p.learningModules ?? extended.learningPathDetails;
  const arr = Array.isArray(mods) ? mods : [];
  if (arr.length > 0) return true;
  const goals = extended.goals;
  return Array.isArray(goals) && goals.length > 0;
}

export const OnboardingCompletionPage: React.FC<{
  userProfile?: UserProfile;
  bio?: Bio;
  learningPath?: LearningPlan;
  sessionId?: string;
  /** When shown inside onboarding flow after consent (no route change); used for polling until profile is created. */
  initialUserEmail?: string;
}> = ({
  userProfile: initialProfile,
  bio: initialBio,
  learningPath: initialPath,
  sessionId: propSessionId,
  initialUserEmail: propUserEmail,
}) => {
  const navigate = useNavigate();
  const params = useParams<{ userEmail?: string }>();
  const [profile, setProfile] = useState<UserProfile | null>(initialProfile || null);
  const [bio, setBio] = useState<Bio | null>(initialBio || null);
  const [path, setPath] = useState<LearningPlan | null>(initialPath || null);
  const [messageIndex, setMessageIndex] = useState(0);
  const [isProfileLoading, setIsProfileLoading] = useState(!initialProfile);
  const [isPathLoading, setIsPathLoading] = useState(!initialPath);
  const [profileTimeout, setProfileTimeout] = useState(false);
  const [pathTimeout, setPathTimeout] = useState(false);
  const [startTime] = useState(Date.now());
  const [winDetail, setWinDetail] = useState<{ name: string; description?: string } | null>(null);
  const [dashboard, setDashboard] = useState<ActivePlanDashboardResponse | null>(null);
  const [activeWins, setActiveWins] = useState<{ id?: string; name: string; description?: string; summary?: string }[] | null>(null);
  const hasAutoNavigatedRef = useRef(false);

  // Fallback to localStorage if sessionId prop is not provided
  const sessionId = propSessionId || (typeof window !== 'undefined' 
    ? window.localStorage.getItem(SESSION_STORAGE_KEY) || undefined
    : undefined);

  // Get user email: URL params, then prop (onboarding flow), then profile, then localStorage
  const userEmail = useMemo(() => {
    const fromRoute = params?.userEmail?.trim();
    if (fromRoute) return decodeURIComponent(fromRoute);
    if (propUserEmail?.trim()) return propUserEmail.trim();
    if (profile?.email) return profile.email;
    if (initialProfile?.email) return initialProfile.email;
    try {
      const raw = localStorage.getItem(LOCAL_USER_INFO_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return parsed.signUpEmail || parsed.email;
      }
    } catch (e) {
      console.error('Failed to parse local user info', e);
    }
    return null;
  }, [params?.userEmail, propUserEmail, profile, initialProfile]);

  // Auto-navigate to learning path when profile (with bio) and learning plan with real content are ready.
  useEffect(() => {
    if (hasAutoNavigatedRef.current || !userEmail) return;
    const hasRealProfile = !!(profile?.userId ?? profile?.email ?? initialProfile?.userId ?? initialProfile?.email);
    const hasBio = !!(profile?.bio ?? initialProfile?.bio ?? bio ?? initialBio);
    const hasRealPath = hasLearningPathContent(path) || hasLearningPathContent(initialPath);
    if (!hasRealProfile || !hasBio || !hasRealPath) return;

    hasAutoNavigatedRef.current = true;
    const t = setTimeout(() => {
      navigate(`/learning-path/${encodeURIComponent(userEmail)}`, { replace: true });
    }, 2500);
    return () => clearTimeout(t);
  }, [userEmail, profile, path, bio, initialProfile, initialPath, initialBio, navigate]);

  // Message rotation effect for setting-up overlay
  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % SETTING_UP_MESSAGES.length);
    }, 3200);
    return () => clearInterval(interval);
  }, []);

  // Polling for profile and bio
  useEffect(() => {
    if (!userEmail) return;
    
    const TIMEOUT_MS = 3 * 60 * 1000; // 3 minutes
    let pollCount = 0;
    const maxPolls = 100; // Increased to allow 3 minutes of polling
    
    // Set timeout for 3 minutes
    const timeoutId = setTimeout(() => {
      if (isProfileLoading) {
        setProfileTimeout(true);
        setIsProfileLoading(false);
      }
    }, TIMEOUT_MS);
    
    const poll = async () => {
      // Check if 3 minutes have passed
      if (Date.now() - startTime >= TIMEOUT_MS) {
        if (isProfileLoading) {
          setProfileTimeout(true);
          setIsProfileLoading(false);
        }
        return true;
      }
      
      try {
        const data = userEmail.includes('@')
          ? await profileApi.getByEmail(userEmail)
          : await profileApi.getById(userEmail);
        if (data) {
          setProfile(data);
          if (data.bio) {
            setBio(data.bio);
            setIsProfileLoading(false);
            clearTimeout(timeoutId);
            return true; // Stop polling profile
          }
        }
      } catch (e) {
        console.debug('Profile poll failed', e);
      }
      pollCount++;
      return pollCount >= maxPolls;
    };

    const interval = setInterval(async () => {
      const shouldStop = await poll();
      if (shouldStop) {
        clearInterval(interval);
        clearTimeout(timeoutId);
      }
    }, 2500);

    poll(); // Initial poll
    return () => {
      clearInterval(interval);
      clearTimeout(timeoutId);
    };
  }, [userEmail, startTime, isProfileLoading]);

  // Polling for learning path
  useEffect(() => {
    if (!userEmail) return;
    
    const TIMEOUT_MS = 3 * 60 * 1000; // 3 minutes
    let pollCount = 0;
    const maxPolls = 100; // Increased to allow 3 minutes of polling
    
    // Set timeout for 3 minutes
    const timeoutId = setTimeout(() => {
      if (isPathLoading) {
        setPathTimeout(true);
        setIsPathLoading(false);
      }
    }, TIMEOUT_MS);
    
    const poll = async () => {
      // Check if 3 minutes have passed
      if (Date.now() - startTime >= TIMEOUT_MS) {
        if (isPathLoading) {
          setPathTimeout(true);
          setIsPathLoading(false);
        }
        return true;
      }
      
      try {
        const data = await learningPlanApi.getByUser(userEmail);
        if (data && hasLearningPathContent(data)) {
          setPath(data);
          setIsPathLoading(false);
          clearTimeout(timeoutId);
          return true;
        }
      } catch (e) {
        console.debug('Path poll failed', e);
      }
      pollCount++;
      return pollCount >= maxPolls;
    };

    const interval = setInterval(async () => {
      const shouldStop = await poll();
      if (shouldStop) {
        clearInterval(interval);
        clearTimeout(timeoutId);
      }
    }, 3000);

    poll(); // Initial poll
    return () => {
      clearInterval(interval);
      clearTimeout(timeoutId);
    };
  }, [userEmail, startTime, isPathLoading]);

  const showLoading = isProfileLoading || isPathLoading;
  const hasTimeout = profileTimeout || pathTimeout;
  const hasUserEmail = !!userEmail;
  const profileWithBio = profile?.bio ?? initialProfile?.bio ?? bio ?? initialBio;
  const pathWithContent = hasLearningPathContent(path) || hasLearningPathContent(initialPath);
  // Show full-screen overlay only until profile + bio are ready. Then show landing with user card and bio; learning path section shows "Building..." until path is ready.
  const showSettingUpOverlay =
    hasUserEmail &&
    !hasTimeout &&
    (!profile?.userId || !profileWithBio);
  const showFullScreenLoading = showLoading && !hasTimeout && !profile && !path && !hasUserEmail;
  const showTimeoutCard = hasTimeout && (!profile || !pathWithContent);
  const showContent = (!!profile && !!profileWithBio) || hasTimeout;
  const pathStillLoading = !pathWithContent && (!!profile && !!profileWithBio) && !hasTimeout;

  // Fallback: set win from learning path when wins API returns empty or errors (so "Welcome" page still shows it)
  const fallbackWinFromPath = useCallback(() => {
    const p = path ?? initialPath;
    const win = p && typeof p === 'object' ? (p as { win?: { winDescription?: string; win_description?: string } }).win : undefined;
    const name = win?.winDescription ?? win?.win_description ?? '';
    if (name.trim()) {
      setWinDetail({ name: name.trim(), description: win?.win_description ?? win?.winDescription });
    } else {
      setWinDetail(null);
    }
  }, [path, initialPath]);

  // Fetch active wins for user (GET .../wins); use first as primary for onboarding summary
  useEffect(() => {
    if (!userEmail || !pathWithContent) return;
    userLearningPlanApi
      .getWinsForUser(userEmail)
      .then((wins) => {
        const first = wins[0];
        if (first) {
          setWinDetail({ name: first.name, description: first.description });
          return;
        }
        fallbackWinFromPath();
      })
      .catch(fallbackWinFromPath);
  }, [userEmail, pathWithContent, fallbackWinFromPath]);

  // When profile is ready, fetch dashboard and wins so we can show full journey on this page
  useEffect(() => {
    if (!userEmail || !showContent) return;
    userLearningPlanApi.getDashboard(userEmail).then(setDashboard).catch(() => setDashboard(null));
    userLearningPlanApi.getWinsForUser(userEmail).then(setActiveWins).catch(() => setActiveWins(null));
  }, [userEmail, showContent]);

  // Learning path display: use friendly title/description when API returns generic "Learning Journey: Goal 1, Goal 2" etc.
  const pathDisplay = useMemo(() => {
    const p = path ?? initialPath;
    if (!p) return { title: 'Your Personalized Learning Path', description: 'A curriculum designed for your career growth and skill acceleration.' };
    const rawTitle = (p.title ?? '').trim();
    const rawDesc = (typeof (p as { description?: string }).description === 'string' ? (p as { description: string }).description : '').trim();
    const looksGeneric = !rawTitle || /Learning Journey/i.test(rawTitle) || /^Goal\s*\d/i.test(rawTitle) || rawTitle === rawDesc;
    const title = looksGeneric ? 'Your Personalized Learning Path' : rawTitle;
    const desc = looksGeneric || !rawDesc
      ? 'A curriculum designed for your career growth and skill acceleration.'
      : rawDesc;
    return { title, description: desc };
  }, [path, initialPath]);

  // Pain points: merge from plan (path.painPoints or path.pain_points) and bio, same as LearningPlanPage / UserLearningPathPage
  const mergedPainPoints = useMemo(() => {
    const planRef = path ?? initialPath;
    const fromPlan = Array.isArray(planRef?.painPoints) ? planRef.painPoints : (planRef as { pain_points?: unknown[] } | null)?.pain_points ?? [];
    const fromBio = bio?.painPoints ?? [];
    const planDescs = fromPlan
      .map((p) => (p as { painPointDescription?: string; pain_point_description?: string }).painPointDescription ?? (p as { pain_point_description?: string }).pain_point_description ?? (p as { description?: string }).description ?? '')
      .filter((s): s is string => typeof s === 'string' && s.trim().length > 0);
    const bioDescs = fromBio
      .map((p) => p.description ?? (p as { painPointDescription?: string }).painPointDescription ?? '')
      .filter((s): s is string => typeof s === 'string' && s.trim().length > 0);
    const seen = new Set<string>();
    const out: string[] = [];
    [...planDescs, ...bioDescs].forEach((s) => {
      const key = s.trim().toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        out.push(s.trim());
      }
    });
    return out;
  }, [path, initialPath, bio]);

  // While backend creates profile, bio, and learning plan, show only an attractive full-screen overlay until all are ready.
  if (showSettingUpOverlay) {
    const step1 = !!profile?.userId;
    const step2 = !!profileWithBio;
    const step3 = pathWithContent;
    const overallPercent = (step1 ? 33 : 0) + (step2 ? 33 : 0) + (step3 ? 34 : 0);
    const steps = [
      { label: 'Creating your profile', done: step1, icon: User },
      { label: 'Generating your AI bio', done: step2, icon: BrainCircuit },
      { label: 'Building your learning path', done: step3, icon: Target },
    ];
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-background via-primary/5 to-violet-500/10 dark:via-primary/10 dark:to-violet-500/20 overflow-hidden relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_20%,hsl(var(--primary)/0.15),transparent_50%)] pointer-events-none" aria-hidden />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_80%_80%,hsl(263_70%_50%/0.08),transparent)] pointer-events-none" aria-hidden />
        <div className="relative z-10 flex flex-col items-center justify-center px-6 py-16 max-w-lg mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="relative w-40 h-40 mb-8"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
              className="absolute inset-0 rounded-full border-4 border-dashed border-primary/40"
            />
            <motion.div
              animate={{ rotate: -360 }}
              transition={{ duration: 14, repeat: Infinity, ease: 'linear' }}
              className="absolute inset-3 rounded-full border-2 border-dashed border-violet-500/30"
            />
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
              className="absolute inset-6 rounded-full flex items-center justify-center bg-primary/15 shadow-lg ring-2 ring-primary/30 ring-offset-2 ring-offset-background"
            >
              <img
                src={fiveIcon}
                alt="Five AI Coach"
                className="h-14 w-14 object-contain drop-shadow-lg"
              />
            </motion.div>
          </motion.div>
          <motion.p
            key={messageIndex}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="text-xl sm:text-2xl font-semibold text-foreground mb-2 min-h-[2.5rem] flex items-center justify-center"
          >
            {SETTING_UP_MESSAGES[messageIndex]}
          </motion.p>
          <p className="text-muted-foreground text-sm mb-6 max-w-sm">
            We're creating your profile, AI bio, and personalized learning path. This usually takes 1–2 minutes. You'll be taken to your learning path automatically.
          </p>

          {/* Overall progress bar */}
          <div className="w-full max-w-sm mb-6">
            <div className="flex justify-between text-sm font-semibold text-foreground/90 mb-2">
              <span>Setting up your experience</span>
              <motion.span key={overallPercent} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="tabular-nums text-primary">
                {overallPercent}%
              </motion.span>
            </div>
            <div className="h-3 w-full rounded-full bg-muted/80 overflow-hidden border border-border/50 shadow-inner">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-primary via-primary to-violet-500 shadow-lg"
                initial={{ width: 0 }}
                animate={{ width: `${overallPercent}%` }}
                transition={{ type: 'spring', stiffness: 50, damping: 20 }}
                style={{ minWidth: 0 }}
              />
            </div>
          </div>

          {/* Step list with checkmarks */}
          <div className="w-full max-w-sm space-y-3 text-left">
            {steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.label}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className={`flex items-center gap-3 rounded-xl px-4 py-3 border transition-all duration-300 ${
                    step.done
                      ? 'bg-primary/10 border-primary/30 dark:bg-primary/20'
                      : 'bg-muted/40 border-border/50'
                  }`}
                >
                  <div
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 transition-all ${
                      step.done ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40 bg-muted/30'
                    }`}
                  >
                    {step.done ? (
                      <CheckCircle2 className="h-5 w-5" />
                    ) : (
                      <Icon className="h-4 w-4 text-primary/80" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium ${step.done ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {step.label}
                    </p>
                  </div>
                  {step.done && (
                    <span className="text-xs font-semibold text-primary uppercase tracking-wider">Done</span>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-slate-50 to-primary/5 dark:via-slate-950 dark:to-primary/10">
      <Header />
      <main className="flex flex-col items-center py-12 pt-24 sm:py-20 sm:pt-32">
        <div className="container-custom max-w-5xl w-full px-4 sm:px-6 space-y-12">
          
          {/* Hero Section */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-4"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-4">
              <Rocket className="w-4 h-4 animate-bounce" />
              <span>Journey Commenced</span>
            </div>
            <h1 className="text-4xl sm:text-6xl font-extrabold tracking-tight text-foreground bg-clip-text text-transparent bg-gradient-to-r from-primary to-violet-600">
              Welcome to Your Career Journey!
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Here&apos;s your onboarding summary — your bio, win, pain points, and learning path.
            </p>
          </motion.div>

          <AnimatePresence mode="wait">
            {showFullScreenLoading ? (
              <motion.div 
                key="loading"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.05 }}
                className="flex flex-col items-center justify-center space-y-8 py-12"
              >
                <div className="relative w-36 h-36">
                  <motion.div 
                    animate={{ rotate: 360 }}
                    transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                    className="absolute inset-0 border-4 border-dashed border-primary/30 rounded-full"
                  />
                  <motion.div 
                    animate={{ rotate: -360 }}
                    transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
                    className="absolute inset-2 border-2 border-dashed border-violet-500/20 rounded-full"
                  />
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                    className="absolute inset-4 rounded-full flex items-center justify-center bg-primary/15 shadow-lg ring-2 ring-primary/30 ring-offset-2 ring-offset-background"
                  >
                    <img src={fiveIcon} alt="Five AI Coach" className="h-10 w-10 object-contain" />
                  </motion.div>
                </div>
                
                <div className="text-center space-y-2">
                  <motion.p 
                    key={messageIndex}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="text-2xl font-medium text-foreground h-8"
                  >
                    {SETTING_UP_MESSAGES[messageIndex]}
                  </motion.p>
                  <p className="text-muted-foreground animate-pulse">Generating your professional future...</p>
                </div>

                <div className="w-full max-w-md space-y-4">
                  <div className="flex justify-between text-sm font-medium">
                    <span>Profile Synthesis</span>
                    <span>{!isProfileLoading ? '100%' : '65%'}</span>
                  </div>
                  <Progress value={!isProfileLoading ? 100 : 65} className="h-2" />
                  
                  <div className="flex justify-between text-sm font-medium">
                    <span>Learning Path Logic</span>
                    <span>{!isPathLoading ? '100%' : '40%'}</span>
                  </div>
                  <Progress value={!isPathLoading ? 100 : 40} className="h-2" />
                </div>
              </motion.div>
            ) : showTimeoutCard ? (
              <motion.div
                key="timeout"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-2xl mx-auto"
              >
                <Card className="border-2 border-amber-500/50 bg-amber-50/50 dark:bg-amber-950/20 shadow-xl">
                  <CardContent className="p-8">
                    <div className="flex flex-col items-center text-center space-y-6">
                      <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center">
                        <AlertTriangle className="w-10 h-10 text-amber-600 dark:text-amber-400" />
                      </div>
                      
                      <div className="space-y-2">
                        <h2 className="text-2xl font-bold text-foreground">
                          Generation Timeout
                        </h2>
                        <p className="text-muted-foreground">
                          We couldn't complete the generation within the expected time.
                        </p>
                      </div>

                      <div className="w-full space-y-4 pt-4">
                        {profileTimeout && (
                          <div className="p-4 rounded-xl bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/50">
                            <div className="flex items-start gap-3">
                              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                              <div className="flex-1 text-left">
                                <p className="font-semibold text-red-900 dark:text-red-100 mb-1">
                                  User Profile Creation Failed
                                </p>
                                <p className="text-sm text-red-700 dark:text-red-300">
                                  The user profile could not be created within 3 minutes. This may indicate an issue with the profile generation service.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {pathTimeout && (
                          <div className="p-4 rounded-xl bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/50">
                            <div className="flex items-start gap-3">
                              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                              <div className="flex-1 text-left">
                                <p className="font-semibold text-red-900 dark:text-red-100 mb-1">
                                  Learning Path Generation Failed
                                </p>
                                <p className="text-sm text-red-700 dark:text-red-300">
                                  The learning path could not be generated within 3 minutes. This may indicate an issue with the learning path generation service.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3 pt-4 w-full">
                        <Button
                          variant="outline"
                          className="flex-1"
                          onClick={() => window.location.reload()}
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Retry Generation
                        </Button>
                        {userEmail && (
                          <Button
                            className="flex-1 bg-primary hover:bg-primary/90 text-white"
                            onClick={() => navigate(`/admin/profiles/${encodeURIComponent(userEmail)}`)}
                          >
                            <User className="w-4 h-4 mr-2" />
                            Check Profile Status
                          </Button>
                        )}
                      </div>

                      <p className="text-xs text-muted-foreground pt-4 border-t w-full">
                        If this issue persists, please contact support or try again later. The generation process may take longer during peak times.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ) : showContent ? (
              <motion.div 
                key="content"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-6"
              >
                {/* Building banner: stay on this page while profile returns 404; we poll until user is created */}
                {!profile && (isProfileLoading || isPathLoading) && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-2xl border-2 border-primary/20 bg-primary/5 dark:bg-primary/10 p-4 sm:p-5"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
                          <Sparkles className="w-5 h-5 text-primary animate-pulse" />
                        </div>
                        <div>
                          <p className="font-semibold text-foreground">
                            We're building your profile and learning path
                          </p>
                          <p className="text-sm text-muted-foreground mt-0.5">
                            This usually takes 1–2 minutes. Stay on this page — we'll update automatically when everything is ready.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground shrink-0">
                        <span className="inline-flex gap-1">
                          <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                          Checking every few seconds…
                        </span>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Timeout Warning Banner - Show if partial timeout */}
                {hasTimeout && (profile || path) && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card className="border-2 border-amber-500/50 bg-amber-50/50 dark:bg-amber-950/20">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="font-semibold text-amber-900 dark:text-amber-100 mb-1">
                              Partial Generation Warning
                            </p>
                            <div className="text-sm text-amber-700 dark:text-amber-300 space-y-1">
                              {profileTimeout && (
                                <p>⚠️ User profile creation timed out after 3 minutes.</p>
                              )}
                              {pathTimeout && (
                                <p>⚠️ Learning path generation timed out after 3 minutes.</p>
                              )}
                              <p className="text-xs mt-2">
                                Some content may be incomplete. You can retry or check back later.
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.location.reload()}
                            className="text-amber-700 dark:text-amber-300"
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Retry
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                <div className="grid gap-8 lg:grid-cols-12">
                {/* Profile Section - Left Column */}
                <div className="lg:col-span-5 space-y-8">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <Card className="overflow-hidden border-0 shadow-2xl bg-card/50 backdrop-blur-md ring-1 ring-white/20 dark:ring-white/10">
                      <div className="h-32 bg-gradient-to-r from-primary to-violet-600 relative">
                        <div className="absolute -bottom-16 left-8">
                          <div className="w-32 h-32 rounded-3xl bg-white dark:bg-slate-900 p-1 shadow-xl">
                            <div className="w-full h-full rounded-[1.25rem] bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 flex items-center justify-center">
                              <User className="w-16 h-12 text-primary/40" />
                            </div>
                          </div>
                        </div>
                      </div>
                      <CardContent className="pt-20 pb-8 px-8 space-y-6">
                        <div className="space-y-1">
                          <h2 className="text-3xl font-bold">
                            {profile?.firstName ? `${profile.firstName} ${profile.lastName || ''}` : userEmail?.split('@')[0] ?? 'You'}
                          </h2>
                          {/* While polling: show building profile and building learning path */}
                          {(isProfileLoading || isPathLoading) && (
                            <div className="rounded-xl bg-primary/10 border border-primary/20 p-3 space-y-2">
                              {isProfileLoading && (
                                <p className="text-sm font-medium text-foreground flex items-center gap-2">
                                  <span className="inline-block w-2 h-2 rounded-full bg-primary animate-pulse" />
                                  Building your profile…
                                </p>
                              )}
                              {isPathLoading && (
                                <p className="text-sm font-medium text-foreground flex items-center gap-2">
                                  <span className="inline-block w-2 h-2 rounded-full bg-primary animate-pulse" />
                                  Building your learning path…
                                </p>
                              )}
                              <p className="text-xs text-muted-foreground">
                                Usually 1–2 minutes. We're checking every few seconds — stay on this page.
                              </p>
                            </div>
                          )}
                          <p className="text-primary font-medium flex items-center gap-2">
                            <Briefcase className="w-4 h-4" />
                            {bio?.currentRole ?? (bio as { currentOccupation?: string })?.currentOccupation ?? (profile ? 'Future Leader' : '—')}
                          </p>
                          {/* Bio summary in card — show summary or profileSummary */}
                          {profile && (bio?.summary ?? bio?.profileSummary ?? (profile.bio as { summary?: string; profileSummary?: string } | undefined)?.summary ?? (profile.bio as { profileSummary?: string })?.profileSummary) && (
                            <div className="rounded-xl bg-muted/50 border border-border/50 p-3 mt-2">
                              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Your bio</p>
                              <p className="text-sm text-foreground line-clamp-4">
                                {bio?.summary ?? bio?.profileSummary ?? (profile.bio as { summary?: string; profileSummary?: string })?.summary ?? (profile.bio as { profileSummary?: string })?.profileSummary ?? ''}
                              </p>
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-3 rounded-2xl bg-slate-100 dark:bg-slate-800/50 space-y-1">
                            <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Experience</p>
                            <p className="font-bold">{bio?.yearsOfExperience || 0}+ Years</p>
                          </div>
                          <div className="p-3 rounded-2xl bg-slate-100 dark:bg-slate-800/50 space-y-1">
                            <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Status</p>
                            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
                              Active
                            </Badge>
                          </div>
                        </div>

                        <div className="space-y-3 pt-2">
                          <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            <Mail className="w-4 h-4" />
                            <span>{userEmail}</span>
                          </div>
                          {profile?.location && (
                            <div className="flex items-center gap-3 text-sm text-muted-foreground">
                              <Globe className="w-4 h-4" />
                              <span>{[profile.location.city, profile.location.country].filter(Boolean).join(', ')}</span>
                            </div>
                          )}
                        </div>

                        <div className="pt-4 flex gap-2">
                          <Button 
                            className="flex-1 bg-primary hover:bg-primary/90 text-white rounded-xl shadow-lg shadow-primary/25"
                            onClick={() => userEmail && pathWithContent && navigate(`/learning-path/${encodeURIComponent(userEmail)}`)}
                            disabled={pathStillLoading}
                          >
                            {pathStillLoading ? (
                              <>
                                <motion.img
                                  src={fiveIcon}
                                  alt=""
                                  className="h-4 w-4 mr-2 object-contain"
                                  animate={{ rotate: 360 }}
                                  transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                                />
                                Preparing…
                              </>
                            ) : (
                              'Go to Learning Path'
                            )}
                          </Button>
                          <div className="flex gap-2">
                            <Button variant="outline" size="icon" className="rounded-xl"><Linkedin className="w-4 h-4" /></Button>
                            <Button variant="outline" size="icon" className="rounded-xl"><Github className="w-4 h-4" /></Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    <Card className="border-0 shadow-xl bg-card/50 backdrop-blur-md">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <BrainCircuit className="w-5 h-5 text-primary" />
                          Top Skills
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="flex flex-wrap gap-2">
                        {bio?.skills?.slice(0, 6).map((skill: { id?: string; name?: string; skillName?: string }, i: number) => (
                          <Badge 
                            key={skill.id ?? i} 
                            variant="secondary" 
                            className="px-3 py-1 rounded-lg bg-primary/5 hover:bg-primary/10 transition-colors"
                          >
                            {skill.name ?? skill.skillName ?? 'Skill'}
                          </Badge>
                        )) || (
                          <p className="text-sm text-muted-foreground italic">Skills synthesis in progress...</p>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                </div>

                {/* Right Column - Bio and Path */}
                <div className="lg:col-span-7 space-y-8">
                  {/* Bio Summary */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <Card className="border-0 shadow-2xl bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-950 ring-1 ring-primary/10">
                      <CardHeader>
                        <div className="flex justify-between items-center">
                          <CardTitle className="text-2xl font-bold flex items-center gap-2">
                            <Sparkles className="w-6 h-6 text-yellow-500" />
                            AI-Powered Professional Summary
                          </CardTitle>
                          <Badge className="bg-primary/10 text-primary hover:bg-primary/20 border-0">World Class Persona</Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="prose dark:prose-invert max-w-none">
                        <div className="relative">
                          <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-primary to-transparent rounded-full opacity-50" />
                          <p className="text-lg leading-relaxed italic text-slate-700 dark:text-slate-300 pl-4">
                            "{bio?.summary ?? bio?.profileSummary ?? (profile?.bio as { summary?: string; profileSummary?: string } | undefined)?.summary ?? (profile?.bio as { profileSummary?: string })?.profileSummary ?? "We're putting the final touches on your professional narrative..."}"
                          </p>
                        </div>
                        {bio?.detailedBio && (
                          <div className="mt-6 p-6 rounded-2xl bg-primary/5 border border-primary/10">
                            <h4 className="text-sm font-bold uppercase tracking-widest text-primary mb-3">Deep Analysis</h4>
                            <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
                              {bio.detailedBio}
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Win (milestone / goal) — from /wins API or path; empty state when none */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                  >
                    <Card className="border-0 shadow-xl bg-gradient-to-br from-amber-50/80 to-orange-50/60 dark:from-amber-950/20 dark:to-orange-950/20 ring-1 ring-amber-200/50 dark:ring-amber-800/30">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Award className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                          Your Win
                        </CardTitle>
                        <CardDescription>Milestone you&apos;re working toward</CardDescription>
                      </CardHeader>
                      <CardContent>
                        {winDetail?.name || (path as { win?: { winDescription?: string; win_description?: string } })?.win?.winDescription || (path as { win?: { win_description?: string } })?.win?.win_description ? (
                          <>
                            <p className="font-semibold text-foreground">
                              {winDetail?.name ?? (path as { win?: { winDescription?: string } })?.win?.winDescription ?? (path as { win?: { win_description?: string } })?.win?.win_description ?? ''}
                            </p>
                            {(winDetail?.description ?? (path as { win?: { win_description?: string; winDescription?: string } })?.win?.win_description ?? (path as { win?: { winDescription?: string } })?.win?.winDescription) && (
                              <p className="text-sm text-muted-foreground mt-2">
                                {winDetail?.description ?? (path as { win?: { win_description?: string } })?.win?.win_description ?? (path as { win?: { winDescription?: string } })?.win?.winDescription ?? ''}
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="text-sm text-muted-foreground">No active wins yet. Your Coach will help you set wins as you go.</p>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Pain Points — from plan (path) and bio, same as Learning Plan page */}
                  {mergedPainPoints.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                    >
                      <Card className="border-0 shadow-xl bg-card/50 backdrop-blur-md">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-orange-500" />
                            Pain Points We&apos;re Addressing
                          </CardTitle>
                          <CardDescription>Challenges your learning path targets</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2">
                            {mergedPainPoints.map((label, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm">
                                <span className="text-muted-foreground mt-1">•</span>
                                <span>{label}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    </motion.div>
                  )}

                  {/* Professional Goals — from bio.professionalGoals */}
                  {(() => {
                    const bioAny = (bio ?? profile?.bio) as Record<string, unknown> | undefined;
                    const rawGoals = (bioAny?.professionalGoals ?? bioAny?.professional_goals ?? []) as string[];
                    // Strip [SHORT_TERM]/[LONG_TERM] prefix for clean display
                    const cleanGoals = rawGoals.map(g => g.replace(/^\[(SHORT_TERM|LONG_TERM)\]\s*/i, ''));
                    return cleanGoals.length > 0 ? (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.45 }}
                      >
                        <Card className="border-0 shadow-xl bg-gradient-to-br from-violet-50/80 to-purple-50/60 dark:from-violet-950/20 dark:to-purple-950/20 ring-1 ring-violet-200/50 dark:ring-violet-800/30">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-lg flex items-center gap-2">
                              <Target className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                              Your Professional Goals
                            </CardTitle>
                            <CardDescription>Personalized goals based on your wins, pain points, and learning path</CardDescription>
                          </CardHeader>
                          <CardContent>
                            <ul className="space-y-2">
                              {cleanGoals.map((goal, i) => (
                                <li key={i} className="flex items-start gap-3">
                                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-violet-500 shrink-0" />
                                  <span className="text-sm text-foreground">{goal}</span>
                                </li>
                              ))}
                            </ul>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ) : null;
                  })()}

                  {/* Learning Path: show "Building..." with Five logo while loading, or full summary when ready */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    {pathStillLoading ? (
                      <Card className="border-0 shadow-2xl bg-card/50 backdrop-blur-md overflow-hidden relative border-2 border-primary/20">
                        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_30%,hsl(var(--primary)/0.08),transparent_50%)]" aria-hidden />
                        <CardHeader>
                          <CardTitle className="text-2xl font-bold flex items-center gap-2 relative z-10">
                            <Trophy className="w-6 h-6 text-primary" />
                            Your Strategic Learning Path
                          </CardTitle>
                          <CardDescription className="relative z-10">We're building your personalized curriculum — usually 1–2 minutes.</CardDescription>
                        </CardHeader>
                        <CardContent className="flex flex-col items-center justify-center py-12 space-y-6 relative z-10">
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                            className="rounded-full flex items-center justify-center w-24 h-24 bg-primary/15 shadow-lg ring-2 ring-primary/30 ring-offset-2 ring-offset-background"
                          >
                            <img src={fiveIcon} alt="Five AI Coach" className="h-12 w-12 object-contain" />
                          </motion.div>
                          <p className="text-lg font-medium text-foreground">Preparing your career journey...</p>
                          <p className="text-sm text-muted-foreground text-center max-w-sm">Stay on this page — we'll update automatically when your learning path is ready.</p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                            Checking every few seconds…
                          </div>
                        </CardContent>
                      </Card>
                    ) : (
                    <Card className="border-0 shadow-2xl bg-card/50 backdrop-blur-md overflow-hidden relative">
                      <div className="absolute top-0 right-0 p-8 opacity-10">
                        <Target className="w-32 h-32" />
                      </div>
                      <CardHeader>
                        <CardTitle className="text-2xl font-bold flex items-center gap-2">
                          <Trophy className="w-6 h-6 text-primary" />
                          Your Strategic Learning Path
                        </CardTitle>
                        <CardDescription>
                          {path?.estimatedDurationHours
                            ? `A ${path.estimatedDurationHours} hour curriculum designed for your success`
                            : 'A personalized curriculum designed for your career growth and skill acceleration.'}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-8">
                        <div className="space-y-4">
                          <h3 className="text-xl font-bold">{pathDisplay.title}</h3>
                          <p className="text-muted-foreground">{pathDisplay.description}</p>
                          {(path ?? initialPath)?.learningModules && (path ?? initialPath)!.learningModules!.length > 0 && (
                            <p className="text-sm font-medium text-primary">
                              {(path ?? initialPath)!.learningModules!.length} module{(path ?? initialPath)!.learningModules!.length !== 1 ? 's' : ''} in your path
                            </p>
                          )}
                        </div>

                        {/* Visual Growth Chart */}
                        <div className="h-48 flex items-end justify-between gap-2 px-4 relative">
                          <div className="absolute inset-0 flex items-center justify-center opacity-5">
                            <span className="text-8xl font-black italic">GROWTH</span>
                          </div>
                          {[40, 65, 55, 85, 70, 95, 100].map((h, i) => (
                            <motion.div 
                              key={i}
                              initial={{ height: 0 }}
                              animate={{ height: `${h}%` }}
                              transition={{ delay: 0.7 + (i * 0.1), duration: 1, ease: "easeOut" }}
                              className="flex-1 rounded-t-lg bg-gradient-to-t from-primary/40 to-primary relative group"
                            >
                              <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-primary text-white text-[10px] font-bold px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                Stage {i+1}
                              </div>
                            </motion.div>
                          ))}
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 pt-4">
                          <div className="flex-1 p-4 rounded-2xl bg-slate-100 dark:bg-slate-800/50 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                              <Calendar className="w-6 h-6" />
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground font-semibold uppercase">Commitment</p>
                              <p className="font-bold">7 Day Jumpstart</p>
                            </div>
                          </div>
                          <div className="flex-1 p-4 rounded-2xl bg-slate-100 dark:bg-slate-800/50 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center text-violet-500">
                              <Target className="w-6 h-6" />
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground font-semibold uppercase">Focus</p>
                              <p className="font-bold">Skill Acceleration</p>
                            </div>
                          </div>
                        </div>

                        <div className="pt-4 space-y-3">
                          <Button 
                            className="w-full h-14 bg-gradient-to-r from-primary to-violet-600 hover:shadow-xl hover:shadow-primary/20 text-lg font-bold rounded-2xl group transition-all"
                            onClick={() => {
                              if (userEmail) {
                                navigate(`/learning-path/${encodeURIComponent(userEmail)}`);
                              }
                            }}
                          >
                            Launch Full Learning Path
                            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                          </Button>
                          <Button 
                            variant="outline"
                            className="w-full h-12 rounded-2xl font-semibold group"
                            onClick={() => navigate('/coach')}
                          >
                            Start AI Coaching
                            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                    )}
                  </motion.div>
                </div>
              </div>

              {/* Full journey summary (same as Coach) when profile is built — show on this page */}
              {profile && (path ?? initialPath) && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="w-full max-w-4xl mx-auto pt-8"
                >
                  <h2 className="text-xl font-bold text-foreground mb-4">Your journey</h2>
                  <CoachJourneySummary
                    profile={profile}
                    learningPlan={path ?? initialPath ?? null}
                    goals={null}
                    dashboard={dashboard ?? undefined}
                    activeWins={activeWins ?? undefined}
                    primaryWin={activeWins?.[0] ? { name: activeWins[0].name, description: activeWins[0].description, summary: activeWins[0].summary } : null}
                    className="rounded-2xl border border-border/80 bg-card/50 p-6"
                  />
                </motion.div>
              )}

              {/* Onboarding summary CTAs — primary next steps */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 justify-center items-stretch sm:items-center w-full max-w-2xl mx-auto pt-8 pb-4"
              >
                <Button
                  size="lg"
                  className="flex-1 min-w-[200px] h-14 text-lg font-bold rounded-2xl bg-gradient-to-r from-primary to-violet-600 hover:shadow-xl hover:shadow-primary/20 transition-all"
                  onClick={() => navigate('/coach')}
                >
                  Go to AI Coach
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="flex-1 min-w-[200px] h-14 text-lg font-bold rounded-2xl border-2 hover:bg-primary/10 hover:border-primary/50 transition-all"
                  onClick={() => userEmail && navigate(`/learning-path/${encodeURIComponent(userEmail)}`)}
                  disabled={pathStillLoading}
                >
                  {pathStillLoading ? (
                    <>
                      <motion.img src={fiveIcon} alt="" className="h-5 w-5 mr-2 object-contain" animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }} />
                      Preparing…
                    </>
                  ) : (
                    <>
                      Go to Learning Path
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
              </motion.div>
              </motion.div>
            ) : null}
          </AnimatePresence>

          {/* Footer Metadata */}
          {(showContent || hasTimeout) && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="flex flex-col items-center gap-4 text-muted-foreground"
            >
              <div className="flex items-center gap-2 text-sm font-medium">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span>All systems operational • AI modules verified</span>
              </div>
              {sessionId && (
                <CopyableId 
                  id={sessionId} 
                  label="Session ID"
                  showLabel={false}
                  className="text-xs opacity-50 hover:opacity-100 transition-opacity"
                  codeClassName="text-xs"
                />
              )}
            </motion.div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default OnboardingCompletionPage;
